import { ICowsList } from 'models/ICattlesData'
import { useCallback, useState } from 'react'

interface UseCheckboxSelectionProps {
	page: number
	cowsList: ICowsList[]
	totalCount: number
	fetchAllCows: () => Promise<ICowsList[]>
}

export const useCheckboxSelection = ({
	page,
	cowsList,
	totalCount,
	fetchAllCows,
}: UseCheckboxSelectionProps) => {
	const [selectedCows, setSelectedCows] = useState<ICowsList[]>([])
	const [selectedCheckboxesByPage, setSelectedCheckboxesByPage] = useState<
		Record<number, number[]>
	>({})
	const [showOnlySelected, setShowOnlySelected] = useState<boolean>(false)
	const [isSelectingAll, setIsSelectingAll] = useState(false)
	const [didSelectAll, setDidSelectAll] = useState(false)

	const isAllSelected =
		didSelectAll || (totalCount > 0 && selectedCows.length === totalCount)

	const toggleCheckbox = useCallback(
		(tagId: number) => {
			const isSelected = selectedCows.some(cow => cow.tag_id === tagId)

			if (isSelected) {
				setDidSelectAll(false)
				setSelectedCows(prev => prev.filter(cow => cow.tag_id !== tagId))
				setSelectedCheckboxesByPage(prev => ({
					...prev,
					[page]: prev[page]?.filter(id => id !== tagId) || [],
				}))
			} else {
				const cow =
					cowsList.find(el => el.tag_id === tagId) ??
					selectedCows.find(el => el.tag_id === tagId)
				if (cow) {
					setSelectedCows(prev => [...prev, cow])
					setSelectedCheckboxesByPage(prev => ({
						...prev,
						[page]: Array.from(new Set([...(prev[page] || []), tagId])),
					}))
				}
			}
		},
		[cowsList, page, selectedCows],
	)

	const toggleAllCheckboxes = useCallback(async () => {
		if (isSelectingAll) return

		if (isAllSelected) {
			setSelectedCows([])
			setSelectedCheckboxesByPage({})
			setShowOnlySelected(false)
			setDidSelectAll(false)
			return
		}

		if (cowsList.length === totalCount && totalCount > 0) {
			const allCows = cowsList.filter(cow => cow.tag_id)
			setSelectedCows(allCows)
			setSelectedCheckboxesByPage({
				[page]: allCows.map(cow => cow.tag_id),
			})
			setDidSelectAll(true)
			return
		}

		setIsSelectingAll(true)
		try {
			const allCows = (await fetchAllCows()).filter(cow => cow.tag_id)
			setSelectedCows(allCows)
			setSelectedCheckboxesByPage({})
			setDidSelectAll(true)
		} finally {
			setIsSelectingAll(false)
		}
	}, [cowsList, fetchAllCows, isAllSelected, isSelectingAll, page, totalCount])

	const resetCheckboxes = useCallback(() => {
		setSelectedCows([])
		setSelectedCheckboxesByPage({})
		setShowOnlySelected(false)
		setDidSelectAll(false)
	}, [])

	return {
		selectedCows,
		selectedCheckboxesByPage,
		showOnlySelected,
		setShowOnlySelected,
		toggleCheckbox,
		toggleAllCheckboxes,
		resetCheckboxes,
		isAllSelected,
		isSelectingAll,
	}
}
